import React, { useMemo, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useIntl } from 'umi';
import { debounce } from 'lodash-es';
import { CapsuleProps, ICapsuleData } from './type.d';
import { DEFAULT_MUTIPLE_GRADIENTS_COLOR, ITEM_MARGIN_BOTTOM, DEFAULT_CAPSULE_COLORS_MAP } from './constants';
import { getTitle } from './util';
import './index.scss';

// Threshold multiplier for overlap detection - accounts for text measurement inaccuracies
const OVERLAP_THRESHOLD_MULTIPLIER = 1.12;

// 文本测量工具
const getTextWidth = (text: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = '14px system-ui';
  return context.measureText(text).width;
};

export default function Capsule(props: CapsuleProps) {
  const {
    data,
    totalData,
    height = 40,
    colors,
    coefficient = 1,
    maxValue = 0,
    dataDirection,
    directionType,
    showZeroText = false,
    zeroPosition = 'auto',
  } = props;
  const [animated, setAnimated] = useState(false);
  const capsuleRef = useRef<HTMLDivElement>(null);
  const [capsuleWidth, setCapsuleWidth] = useState(0);
  const { formatMessage } = useIntl();

  // useEffect(() => {
  //   console.log('Capsule data snapshot:', data);
  //   console.log('showZeroText:', showZeroText);
  // }, [data, showZeroText]);
  
  // 初始化容器宽度
  useLayoutEffect(() => {
    if (capsuleRef.current) {
      setCapsuleWidth(capsuleRef.current.clientWidth);
    }
  }, []);
  
  // 监听容器尺寸变化
  useEffect(() => {
    if (!capsuleRef.current) return;
    
    let isMounted = true; // Add mounted flag to prevent state updates after unmount
    
    const resizeObserver = new ResizeObserver(debounce((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.width && isMounted) { // Only update state if component is still mounted
          setCapsuleWidth(entry.contentRect.width);
        }
      }
    }, 200));
    
    resizeObserver.observe(capsuleRef.current);
    return () => {
      isMounted = false; // Mark as unmounted
      resizeObserver.disconnect();
    };
  }, []);
  
  // 防抖函数
  // const debounce = (func: Function, delay: number) => {
  //   let timer: NodeJS.Timeout;
  //   return (...args: any[]) => {
  //     clearTimeout(timer);
  //     timer = setTimeout(() => func.apply(this, args), delay);
  //   };
  // };

  // 计算最大值
  const maxDisplayValue = useMemo(() => {
    // 优先使用外部传入的 maxValue
    if (!!maxValue) {
      return maxValue * coefficient;
    }
    // 否则基于当前组件的 data 计算
    const max = Math.max(...data.map(p => Math.abs(p.value)), totalData ? Math.abs(totalData.value) : 0);
    return max * coefficient;
  }, [data, totalData, coefficient, maxValue]);

  // 计算位置和尺寸
  const calculatePosition = (value: number) => {
    const denominator = Math.abs(totalData?.liubaiValue || totalData?.value);
    // 避免 denominator 为 0 时出现 NaN/Infinity，保证宽度计算安全
    if (!denominator) {
      return {
        width: '0%',
        isNegative: value < 0,
      };
    }
    const percentage = (Math.abs(value) / denominator) * 100;
    return {
      width: `${percentage}%`,
      isNegative: value < 0,
    };
  };

  // 单轴模式 - 排序柱子（大的在上层）
  const sortedPillars = useMemo(
    () =>
      data
        // 复制数组，避免在排序时直接修改 props 内的原始数据
        .slice()
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
        .map((item) => ({
          ...item,
          ...calculatePosition(item.value),
        })),
    [data],
  );

  // Check if all pillar values are equal (used to flip bar z-index so first capsule is on top)
  const allValuesEqual = useMemo(() => {
    if (data.length <= 1) return false;
    return data.every((item) => Math.abs(item.value) === Math.abs(data[0].value));
  }, [data]);

  // largest absolute value across the current capsule
  const maxAbsPillarValue = useMemo(() => {
    if (!data?.length) {
      return 0;
    }
    return data.reduce((max, item) => {
      const absValue = Math.abs(item.value || 0);
      return absValue > max ? absValue : max;
    }, 0);
  }, [data]);

  // Check if all dynamic pillars share the same absolute value (i.e., all are "largest")
  const allDynamicPillarsTied = useMemo(() => {
    const dynamicPillars = data.filter(item => item.insideAlign === 'dynamic');
    if (dynamicPillars.length <= 1) return false;
    return dynamicPillars.every(item => Math.abs(item.value || 0) === maxAbsPillarValue);
  }, [data, maxAbsPillarValue]);

  const hasNonZeroValue = useMemo(
    () => sortedPillars.some((item) => item.value !== 0),
    [sortedPillars],
  );

  // 计算柱子位置
  const calculatePillarPosition = (value: number) => {
    // 同向的时候，要把0点轴放在中间
    if (directionType === 'centerInSameDirection') {
      // maxDisplayValue 可能由于数据为空而为 0，此时直接返回宽度 0，避免除以 0
      if (!maxDisplayValue) {
        return {
          width: 0,
          left: 50,
          direction: 'right' as const,
        };
      }
      if (value >= 0) {
        return {
          width: (Math.abs(value) / maxDisplayValue) * 50,
          left: 50,
          direction: 'right' as const,
        };
      }
      const width = (Math.abs(value) / maxDisplayValue) * 50;
      return {
        width,
        left: 50 - width,
        direction: 'left' as const,
      };
    }
    // 正常的正向轴，就是0点在最左侧
    if (dataDirection === 'positive') {
      // 正向轴同样需要兜底，防止缺失数据造成的 NaN
      if (!maxDisplayValue) {
        return {
          width: 0,
          left: 0,
          direction: 'right' as const,
        };
      }
      return {
        width: (Math.abs(value) / maxDisplayValue) * 100,
        left: 0,
        direction: 'right' as const,
      };
    }
    // 正常的负向轴，0点在最右侧
    if (dataDirection === 'negative') {
      // 负向轴防止 Infinity/NaN
      if (!maxDisplayValue) {
        return {
          width: 0,
          left: 100,
          direction: 'left' as const,
        };
      }
      const width = (Math.abs(value) / maxDisplayValue) * 100;
      return {
        width,
        left: 100 - width,
        direction: 'left' as const,
      };
    }
    // 混合值
    if (value > 0) {
      if (!maxDisplayValue) {
        // 混合模式同样兜底
        return {
          width: 0,
          left: 50,
          direction: 'right' as const,
        };
      }
      return {
        width: (Math.abs(value) / maxDisplayValue) * 50,
        left: 50,
        direction: 'right' as const,
      };
    }
    if (value === 0) {
      if (zeroPosition === 'start') {
        return {
          width: 0,
          left: 0,
          direction: 'right' as const,
        };
      }
      // if (zeroPosition === 'end') {
      //   return {
      //     width: 0,
      //     left: 100,
      //     direction: 'left' as const,
      //   };
      // }
      if (dataDirection === 'mixed') {
        //   // 混合模式下，根据数据方向确定0点位置
        const dominantDirection: 'left' | 'right' =
          sortedPillars.some((item) => item.value < 0) ? 'left' : 'right';
        return {
          width: 0,
          left: 50,
          direction: dominantDirection,
        };
      }
      // if (dataDirection === 'negative') {
      //   return {
      //     width: 0,
      //     left: 100,
      //     direction: 'left' as const,
      //   };
      // }
      return {
        width: 0,
        left: 0,
        direction: 'right' as const,
      };
    }
    if (!maxDisplayValue) {
      return {
        width: 0,
        left: 50,
        direction: 'left' as const,
      };
    }
    const width = (Math.abs(value) / maxDisplayValue) * 50;
    return {
      width,
      left: 50 - width,
      direction: 'left' as const
    };
  };

  // 获取渐变颜色
  const getGradient = (index: number, color: string, isNegative: boolean) => {
    let { start, end } = DEFAULT_MUTIPLE_GRADIENTS_COLOR[index];
    // 可使用外部传入的color
    if (color) {
      const [customStart, customEnd] = color.split(',') || '';
      start = customStart;
      end = customEnd || customStart;
    }
    return `linear-gradient(to right, ${isNegative ? end : start}, ${isNegative ? start : end})`;
  };

  // Calculate title positions for multiple bars and check for overlaps
  const multipleBarTitlePositions = useMemo(() => {
    // Only process if we have multiple bars
    if (data.length <= 1 || !capsuleRef.current || capsuleWidth === 0) {
      return null;
    }

    const currentCapsuleWidth = capsuleWidth || capsuleRef.current?.clientWidth || 0;
    const SPACING = 20; // Spacing between titles when positioned outside (in pixels)

    // Calculate where each title would be positioned if inside
    const insidePositions: Array<{
      item: ICapsuleData;
      pillarIndex: number;
      textWidth: number;
      capsuleBarLeft: number; // Left edge of the capsule bar in pixels
      capsuleBarWidth: number; // Width of the capsule bar in pixels
      titleLeft: number; // Left edge of title text if positioned inside
      titleRight: number; // Right edge of title text if positioned inside
      isPositive: boolean;
      position: ReturnType<typeof calculatePillarPosition>;
    }> = [];

    sortedPillars.forEach((pillar, index) => {
      const numericValue = typeof pillar.value === 'string' ? Number(pillar.value) : pillar.value;
      const position = calculatePillarPosition(numericValue);
      const isPositive = numericValue > 0;
      const isZeroValue = numericValue === 0;
      const capsuleBarWidth = (position.width / 100) * currentCapsuleWidth;
      const capsuleBarLeft = (position.left / 100) * currentCapsuleWidth;
      
      const titleText = getTitle(pillar);
      const textWidth = getTextWidth(titleText);
      
      // Calculate where title would be if positioned inside
      let titleLeft: number;
      let titleRight: number;
      
      // Special handling for zero values when showZeroText is true
      if (isZeroValue && showZeroText) {
        // Zero values use fixed positioning: left: '6px' for right direction, right: '53px' for left direction
        if (position.direction === 'left') {
          // Right side positioning: right: '53px' means 53px from the right edge
          titleRight = currentCapsuleWidth - 53;
          titleLeft = titleRight - textWidth;
        } else {
          // Left side positioning: left: '6px' means 6px from the left edge
          titleLeft = 6;
          titleRight = titleLeft + textWidth;
        }
      } else {
        // For dynamic alignment, largest goes to end (right for positive, left for negative)
        const isLargest = Math.abs(numericValue) >= maxAbsPillarValue;
        
        // When all dynamic pillars are tied, use index-based positioning
        // instead of treating all as "largest"
        let effectiveIsLargest = isLargest;
        if (allDynamicPillarsTied && pillar.insideAlign === 'dynamic') {
          const dynamicItems = data.filter(d => d.insideAlign === 'dynamic');
          const originalIndex = dynamicItems.findIndex(d => d.title === pillar.title && d.value === pillar.value);
          // First dynamic item → start (not largest), last → end (largest)
          effectiveIsLargest = originalIndex > 0;
        }
        
        if (effectiveIsLargest) {
          // Positioned at end (right for positive, left for negative)
          if (isPositive) {
            titleRight = capsuleBarLeft + capsuleBarWidth - 10; // 10px padding from right
            titleLeft = titleRight - textWidth;
          } else {
            titleLeft = capsuleBarLeft + 6; // 6px padding from left
            titleRight = titleLeft + textWidth;
          }
        } else {
          // Positioned at start (left for positive, right for negative)
          if (isPositive) {
            titleLeft = capsuleBarLeft + 6; // 6px padding from left
            titleRight = titleLeft + textWidth;
          } else {
            titleRight = capsuleBarLeft + capsuleBarWidth - 4; // 4px padding from right
            titleLeft = titleRight - textWidth;
          }
        }
      }

      insidePositions.push({
        item: pillar,
        pillarIndex: index,
        textWidth,
        capsuleBarLeft,
        capsuleBarWidth,
        titleLeft,
        titleRight,
        isPositive,
        position,
      });
    });

    // Find the longest bar width
    const longestBarWidth = Math.max(...insidePositions.map(pos => pos.capsuleBarWidth));
    
    // Calculate total text width (sum of all title widths)
    const totalTextWidth = insidePositions.reduce((sum, pos) => sum + pos.textWidth, 0);
    
    // Check if all values have the same sign (all positive or all negative)
    // If mixed (some positive, some negative), they won't overlap since they're on opposite sides
    const hasPositiveValue = insidePositions.some(pos => pos.item.value > 0);
    const hasNegativeValue = insidePositions.some(pos => pos.item.value < 0);
    const hasMixedSigns = hasPositiveValue && hasNegativeValue;
    
    // Check if titles overlap: if total text width (with threshold) > longest bar width, they must overlap
    // Skip overlap logic if values have mixed signs (positive and negative won't overlap)
    const hasOverlap = !hasMixedSigns && (totalTextWidth * OVERLAP_THRESHOLD_MULTIPLIER > longestBarWidth);
    
    // console.log('🔍 Overlap Check:', {
    //   longestBarWidth: longestBarWidth.toFixed(2),
    //   totalTextWidth: totalTextWidth.toFixed(2),
    //   hasMixedSigns,
    //   hasOverlap,
    //   titles: insidePositions.map(pos => ({
    //     title: getTitle(pos.item),
    //     textWidth: pos.textWidth.toFixed(2),
    //     value: pos.item.value,
    //   })),
    // });

    // If no overlap, return null (use normal positioning)
    if (!hasOverlap) {
      return null;
    }

    // If overlap detected, calculate outside positions with spacing
    const outsidePositions: Map<number, { left?: string; right?: string }> = new Map();

    // Find the longest bar (the one with the largest width) - this is our reference point
    const longestBar = insidePositions.reduce((longest, current) => 
      current.capsuleBarWidth > longest.capsuleBarWidth ? current : longest
    );
    
    // Determine if we're dealing with negative direction (bars grow from right to left)
    // This includes: pure negative direction, OR centerInSameDirection/mixed where all values are negative
    const allValuesNegative = insidePositions.every(pos => pos.item.value < 0);
    const isNegativeDirection = dataDirection === 'negative' || 
      ((directionType === 'centerInSameDirection' || dataDirection === 'mixed') && allValuesNegative);

    // Calculate available space outside the longest bar
    // For centerInSameDirection, the capsule only uses half the width (0 is at middle)
    const availableOutsideSpace = directionType === 'centerInSameDirection' 
      ? (currentCapsuleWidth / 2) - longestBar.capsuleBarWidth
      : currentCapsuleWidth - longestBar.capsuleBarWidth;
    
    // Check if all titles can fit outside
    const canFitAllOutside = totalTextWidth * OVERLAP_THRESHOLD_MULTIPLIER + (SPACING * insidePositions.length) <= availableOutsideSpace;

    // console.log('📊 Capsule Overlap Detection:', {
    //   hasOverlap,
    //   directionType,
    //   dataDirection,
    //   allValuesNegative,
    //   isNegativeDirection,
    //   currentCapsuleWidth,
    //   availableOutsideSpace: availableOutsideSpace.toFixed(2),
    //   totalTextWidthWithSpacing: (totalTextWidth + (SPACING * insidePositions.length)).toFixed(2),
    //   canFitAllOutside,
    //   bars: insidePositions.map(pos => ({
    //     title: getTitle(pos.item),
    //     value: pos.item.value,
    //     barLeft: pos.capsuleBarLeft,
    //     barWidth: pos.capsuleBarWidth,
    //     barRight: pos.capsuleBarLeft + pos.capsuleBarWidth,
    //     textWidth: pos.textWidth,
    //   })),
    //   longestBar: {
    //     title: getTitle(longestBar.item),
    //     barLeft: longestBar.capsuleBarLeft,
    //     barWidth: longestBar.capsuleBarWidth,
    //   },
    // });
    
    // Sort by bar width (ascending) - smaller bar's title first, bigger bar's title second
    const sortedByWidth = [...insidePositions].sort((a, b) => 
      a.capsuleBarWidth - b.capsuleBarWidth
    );

    if (!canFitAllOutside) {
      // Not enough space outside - put longest bar's title outside, smaller bars' titles inside
      // Only position the longest bar outside, smaller bars stay inside with default positioning
      
      if (isNegativeDirection) {
        let currentRightOffset = longestBar.capsuleBarWidth + SPACING;
        console.log(`  📍 Positioning "${getTitle(longestBar.item)}": right=${currentRightOffset}px (textWidth=${longestBar.textWidth}) [longest outside]`);
        outsidePositions.set(longestBar.pillarIndex, {
          right: `${currentRightOffset}px`,
        });
      } else {
        let currentOffset = longestBar.capsuleBarWidth + SPACING;
        console.log(`  📍 Positioning "${getTitle(longestBar.item)}": left=${currentOffset}px (textWidth=${longestBar.textWidth}) [longest outside]`);
        outsidePositions.set(longestBar.pillarIndex, {
          left: `${currentOffset}px`,
        });
      }
      // Smaller bars are NOT added to outsidePositions, so they will use default inside positioning
    } else if (isNegativeDirection) {
      // For negative direction: bars grow from right to left
      // Position titles to the LEFT of the longest bar
      let currentRightOffset = longestBar.capsuleBarWidth + SPACING;

      sortedByWidth.forEach((pos) => {
        console.log(`  📍 Positioning "${getTitle(pos.item)}": right=${currentRightOffset}px (textWidth=${pos.textWidth})`);
        // Position titles sequentially to the left of the longest bar
        outsidePositions.set(pos.pillarIndex, {
          right: `${currentRightOffset}px`,
        });
        currentRightOffset += pos.textWidth + SPACING;
      });
    } else {
      // For positive/mixed/centerInSameDirection: bars grow from left to right
      // Position titles to the RIGHT of the longest bar
      let currentOffset = longestBar.capsuleBarWidth + SPACING;

      sortedByWidth.forEach((pos) => {
        console.log(`  📍 Positioning "${getTitle(pos.item)}": left=${currentOffset}px (textWidth=${pos.textWidth})`);
        // Position titles sequentially to the right of the longest bar with spacing
        outsidePositions.set(pos.pillarIndex, {
          left: `${currentOffset}px`,
        });
        currentOffset += pos.textWidth + SPACING;
      });
    }

    return outsidePositions;
  }, [data, sortedPillars, capsuleWidth, maxAbsPillarValue, allDynamicPillarsTied, maxDisplayValue, dataDirection, directionType, showZeroText]);

  const getTitlePosition = (
    item: ICapsuleData,
    capsulePercent: number,
    pillarIndex?: number,
    position?: ReturnType<typeof calculatePillarPosition>,
  ): { left?: string; right?: string } => {
    const { titlePosition, value, left: customLeft, right: customRight, insideAlign } = item;
    const isPositive = value > 0;
    const isZeroValue = value === 0;
    const titleText = getTitle(item);
    const textWidth = getTextWidth(titleText);
    // Get width from ref if state is not yet initialized
    const currentCapsuleWidth = capsuleWidth || capsuleRef.current?.clientWidth || 0;
    const capsuleBarWidth = (capsulePercent / 100) * currentCapsuleWidth;
    const availableWidth = capsuleBarWidth - 20; // For internal positioning calculations
    const canFitTextInside = availableWidth >= textWidth;
    
    // Check if we have multiple bars with overlapping titles - use pre-calculated outside positions
    if (multipleBarTitlePositions && pillarIndex !== undefined) {
      const outsidePosition = multipleBarTitlePositions.get(pillarIndex);
      if (outsidePosition) {
        return outsidePosition;
      }
    }
    
    // Special handling for zero values when showZeroText is true
    if (isZeroValue && showZeroText && position) {
      return position.direction === 'left'
        ? { right: customRight || '53px', left: customLeft || 'unset' }
        : { left: customLeft || '6px', right: customRight || 'unset' };
    }
    
    // console.log('getTitlePosition:', {
    //   titleText,
    //   textWidth,
    //   capsuleBarWidth,
    //   availableWidth,
    //   capsulePercent,
    //   capsuleWidth,
    //   currentCapsuleWidth,
    //   capsuleRefWidth: capsuleRef.current?.clientWidth,
    //   canFitTextInside,
    //   textWidthLessThanCapsule: textWidth < capsuleBarWidth,
    //   hasMultipleBarOverlap: multipleBarTitlePositions !== null
    // });
    
    // 自定义位置优先
    if (titlePosition === 'custom') {
      if (customLeft) return { left: customLeft };
      if (customRight) return { right: customRight };
    }
    
    // 外部位置
    if (titlePosition === 'outside') {
      return isPositive 
        ? { left: customLeft || 'calc(100% + 6px)' }
        : { right: customRight || 'calc(100% + 6px)' };
    }
    
    // 如果明确指定了 insideAlign，优先使用
    if (insideAlign === 'start') {
      return isPositive 
        ? { left: customLeft || '6px' }
        : { right: customRight || '4px' };
    }
    
    if (insideAlign === 'end') {
      if (!canFitTextInside) {
        return isPositive
          ? { left: customLeft || '6px' }
          : { right: customRight || '4px' };
      }
      return isPositive 
        ? { right: customRight || '10px' }
        : { left: customLeft || '10px' };
    }
    
    if (insideAlign === 'dynamic') {
      // In SingleBar with centerInSameDirection:
      // - Same direction pillars (both +/both -): bars overlap → text stays inside
      //   (visually within the larger bar). multipleBarTitlePositions handles title collisions.
      // - Mixed direction pillars (one +, one -): bars go opposite ways, no overlap → if text
      //   can't fit inside its own capsule, position it outside with capsule color.
      // NOTE: We check pillar values directly (not dataDirection which includes totalData/KPI).
      const pillarValues = data.filter(d => d.type === 'pillar' && d.value !== null && d.value !== undefined);
      const hasPositivePillar = pillarValues.some(d => d.value > 0);
      const hasNegativePillar = pillarValues.some(d => d.value < 0);
      const pillarsHaveMixedSigns = hasPositivePillar && hasNegativePillar;
      const barsDoNotOverlap =
        directionType === 'centerInSameDirection' && pillarsHaveMixedSigns;

      // When all dynamic pillars have the same absolute value, they are all "largest"
      // which would cause overlap. Fall back to index-based positioning:
      // first dynamic item → 'start', last dynamic item → 'end'
      if (allDynamicPillarsTied) {
        // When bars don't overlap (mixed direction) + tied values,
        // each bar is independent — always position at end or outside
        if (barsDoNotOverlap) {
          if (canFitTextInside) {
            return isPositive
              ? { right: customRight || '10px' }
              : { left: customLeft || '10px' };
          }
          return isPositive
            ? { left: customLeft || 'calc(100% + 6px)' }
            : { right: customRight || 'calc(100% + 6px)' };
        }

        // When bars overlap (same direction) + tied values, fall back to
        // index-based positioning: first dynamic → 'start', last → 'end'
        const dynamicItems = data.filter(d => d.insideAlign === 'dynamic');
        const originalIndex = dynamicItems.findIndex(d => d.title === item.title);
        const isFirst = originalIndex === 0;
        if (isFirst) {
          return isPositive 
            ? { left: customLeft || '6px' }
            : { right: customRight || '4px' };
        } else {
          if (!canFitTextInside) {
            return isPositive
              ? { left: customLeft || '6px' }
              : { right: customRight || '4px' };
          }
          return isPositive 
            ? { right: customRight || '10px' }
            : { left: customLeft || '10px' };
        }
      }

      const isLargest = Math.abs(value) >= maxAbsPillarValue;

      // When bars don't overlap (mixed direction), each bar is independent:
      // - canFitTextInside → position at the END of the capsule
      // - !canFitTextInside → position outside the capsule with capsule color
      if (barsDoNotOverlap) {
        if (canFitTextInside) {
          return isPositive
            ? { right: customRight || '10px' }
            : { left: customLeft || '10px' };
        }
        return isPositive
          ? { left: customLeft || 'calc(100% + 6px)' }
          : { right: customRight || 'calc(100% + 6px)' };
      }

      if (isLargest && canFitTextInside) {
        return isPositive
          ? { right: customRight || '10px' }
          : { left: customLeft || '10px' };
      }
      return isPositive
        ? { left: customLeft || '6px' }
        : { right: customRight || '4px' };
    }
    
    if (!capsuleRef.current || currentCapsuleWidth === 0) {
      // If capsuleWidth is not yet initialized, default to inside positioning
      return isPositive 
        ? { right: customRight || '10px' }
        : { left: customLeft || '10px' };
    }
    // 自动计算文本位置（当没有指定 insideAlign 时）
    // If text can fit inside (textWidth <= availableWidth), position it inside
    // Otherwise, position it outside
    if (canFitTextInside) {
      // Text can fit inside - position inside
      return isPositive 
        ? { right: customRight || '10px' }
        : { left: customLeft || '10px' };
    }
    // Text cannot fit inside - position outside
    return isPositive 
      ? { left: customLeft || 'calc(100% + 6px)' }
      : { right: customRight || 'calc(100% + 6px)' };
  };

  // 动画效果
  useEffect(() => {
    let isMounted = true; // Add mounted flag
    
    setAnimated(false);
    const timer = setTimeout(() => {
      if (isMounted) { // Only update state if component is still mounted
        setAnimated(true);
      }
    }, 200);
    
    return () => {
      isMounted = false; // Mark as unmounted
      clearTimeout(timer);
    };
  }, [data, totalData]); // Re-run animation when data or totalData values change

  const containerBorderRadius = useMemo(() => {
    if (directionType === 'centerInSameDirection' || dataDirection === 'mixed') {
      return '20px';
    }
    if (dataDirection === 'negative') {
      return '20px 0 0 20px';
    }
    return '0 20px 20px 0'; // top right and bottom right having border radius 20px
  }, [dataDirection, directionType]);

  return (
    <div
      ref={capsuleRef}
      className='capsule'
      style={{
        height: height || '40px',
        marginBottom: `${ITEM_MARGIN_BOTTOM}px`,
        borderRadius: containerBorderRadius,
      }}
    >
      {/* 渲染柱子 */}
      {sortedPillars.map((pillar, index) => {
        const { title, value, isHide, colorType = 'skyBlue', isBold = false, titlePosition } = pillar;
        const targetColorMap = DEFAULT_CAPSULE_COLORS_MAP[colorType];
        const markColor = targetColorMap?.capsule;
        const baseTextColor = targetColorMap?.text || '#fff';
        const numericValue =
          typeof value === 'string' ? Number(value) : value;

        const isZeroNumericValue = numericValue === 0;

        const position = calculatePillarPosition(numericValue);
        const isNegative = numericValue < 0;
        const isZeroValue = isZeroNumericValue;
        const adjustedWidthPercent = isZeroValue && showZeroText
          ? Math.max(position.width, 1)
          : position.width;

        // Check if we have multiple bars with overlapping titles - use pre-calculated outside positions
        // This applies to both zero and non-zero values
        const outsidePosition = multipleBarTitlePositions && multipleBarTitlePositions.has(index);
        
        // Get title style: outsidePosition handles overlapping cases (including multiple bar zero values),
        // otherwise getTitlePosition handles normal positioning (including single zero value case)
        const titleStyle = outsidePosition
          ? multipleBarTitlePositions.get(index)!
          : getTitlePosition(
              pillar,
              position.width,
              index,
              position,
            );
        
        // Automatically detect if title is positioned outside based on the style values
        // Check if this title is in the multipleBarTitlePositions map (positioned outside due to overlap)
        const isPositionedOutsideByOverlap = multipleBarTitlePositions && multipleBarTitlePositions.has(index);
        
        // Outside positioning uses 'calc(100% + 6px)' or pixel values greater than capsule width
        const currentCapsuleWidthForDetection = capsuleWidth || capsuleRef.current?.clientWidth || 0;
        const isTitleOutside = 
          titlePosition === 'outside' ||
          isPositionedOutsideByOverlap ||
          (titleStyle.left && (
            titleStyle.left.includes('calc(100%') || 
            titleStyle.left.includes('100%') ||
            (titleStyle.left.includes('px') && parseFloat(titleStyle.left) > currentCapsuleWidthForDetection)
          )) ||
          (titleStyle.right && (
            titleStyle.right.includes('calc(100%') || 
            titleStyle.right.includes('100%') ||
            (titleStyle.right.includes('px') && parseFloat(titleStyle.right) > currentCapsuleWidthForDetection)
          ));
        
        // Use base text color if explicitly set to outside OR automatically positioned outside
        const shouldUseBaseTextColor =
          isTitleOutside ||
          (isZeroNumericValue && showZeroText && !hasNonZeroValue);
        const textColor = shouldUseBaseTextColor ? baseTextColor : undefined;
  
        // 根据方向设置不同的初始宽度和位置
        const initialStyle = {
          width: '0',
          left: isNegative ? `${position.left + position.width}%` : `${position.left}%`
        };
  
        // Only render the text when:
        // 1) the pillar is visible, and
        // 2) the value is a real number, or
        // 3) the value is explicitly zero (number or string) and zero-labels are allowed.
        const shouldDisplayText =
          !isHide &&
          (value !== null && value !== undefined
            ? true
            : showZeroText && (value === 0 || value === '0'));

        return (
          <React.Fragment key={`${title}-${index}`}>
            {/* 胶囊背景部分 - 有动画 */}
            <div
              className="capsule-pillar-bg"
              style={{
                height,
                width: animated ? `${position.width}%` : initialStyle.width,
                left: animated ? `${position.left}%` : initialStyle.left,
                background: getGradient(index, markColor, value <= 0),
                borderRadius: dataDirection === 'mixed' ? 
                  isNegative ? '30px 0 0 30px' : '0 30px 30px 0' :
                  isNegative ? '30px 0 0 30px' : '0 30px 30px 0',
                zIndex: allValuesEqual ? 10 + (sortedPillars.length - 1 - index) : 10 + index,
                transition: `width 0.8s cubic-bezier(0.22, 0.61, 0.36, 1) ${index * 0.2}s, 
                            left 0.8s cubic-bezier(0.22, 0.61, 0.36, 1) ${index * 0.2}s`,
                position: 'absolute',
                display: isHide ? 'none' : 'block'
              }}
            />
            
            {/* 文字部分 - 无动画，始终显示 */}
            {shouldDisplayText && (
              <div
                className="capsule-pillar-text-wrap"
                style={{
                  position: 'absolute',
                  height,
                  left: `${position.left}%`,
                  width: isZeroValue && showZeroText ? 'auto' : `${adjustedWidthPercent}%`,
                  minWidth: isZeroValue && showZeroText ? '48px' : undefined,
                  zIndex: 20 + index,
                  display: 'flex',
                  justifyContent: (() => {
                    if (isZeroValue && showZeroText) {
                      return position.direction === 'right' ? 'flex-start' : 'flex-end';
                    }
                    return position.direction === 'right' ? 'flex-end' : 'flex-start';
                  })(),
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div
                  className={`capsule-pillar-text`}
                  style={{
                    color: textColor,
                    ...titleStyle,
                    fontWeight: isBold ? 'bold' : 'normal',
                    lineHeight: `${height}px`,
                  }}>
                  {getTitle(
                    pillar,
                    pillar?.localeTitleKey && pillar.localeTitleKey.startsWith('dbs_')
                      ? formatMessage({ id: pillar.localeTitleKey })
                      : undefined,
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};